import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
    Avatar,
    AppBar,
    Badge,
    Box,
    Button,
    ButtonBase,
    CircularProgress,
    Divider,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Menu,
    Stack,
    Toolbar,
    Typography,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";

import { AuthContext } from "../auth/AuthContext";
import logo from "../components/assets/logo.png";
import axios from "../api/axios";
import {
    formatNotificationDate,
    getNotificationPath,
    getNotificationSeverityColor,
    NOTIFICATION_REFRESH_EVENT,
} from "../modules/notifications/notificationUtils";

const defaultPathByRole = {
    FIELD_TECH: "/tasks",
    POS_TECHNICIAN: "/pos/tickets",
    INVENTORY_ADMIN: "/repair-warranty",
    OFFICE_TECH: "/repair-warranty",
};

export default function Topbar({ toggleSidebar, mobileOpen }) {
    const { user, logout } = useContext(AuthContext);
    const navigate = useNavigate();
    const [notificationsAnchor, setNotificationsAnchor] = useState(null);
    const [userMenuAnchor, setUserMenuAnchor] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [notificationTotal, setNotificationTotal] = useState(0);
    const [notificationLoading, setNotificationLoading] = useState(false);
    const [notificationError, setNotificationError] = useState("");

    const fetchNotifications = useCallback(async () => {
        if (!user) {
            setNotifications([]);
            setUnreadCount(0);
            setNotificationTotal(0);
            setNotificationError("");
            return;
        }

        try {
            setNotificationLoading(true);
            setNotificationError("");
            const [feedRes, countRes] = await Promise.all([
                axios.get("/notifications", {
                    params: {
                        scope: "ALL",
                        page: 1,
                        limit: 2,
                    },
                }),
                axios.get("/notifications/unread-count"),
            ]);

            setNotifications(feedRes.data?.items || []);
            setNotificationTotal(Number(feedRes.data?.total || 0));
            setUnreadCount(Number(countRes.data?.count || 0));
        } catch {
            setNotificationError("Unable to load notifications.");
        } finally {
            setNotificationLoading(false);
        }
    }, [user]);

    useEffect(() => {
        fetchNotifications();
        const timer = setInterval(fetchNotifications, 60000);
        const handleRefresh = () => {
            fetchNotifications();
        };

        window.addEventListener(NOTIFICATION_REFRESH_EVENT, handleRefresh);

        return () => {
            clearInterval(timer);
            window.removeEventListener(NOTIFICATION_REFRESH_EVENT, handleRefresh);
        };
    }, [fetchNotifications]);

    if (!user) return null;

    const handleBrandClick = () => {
        navigate(defaultPathByRole[user.role] || "/dashboard");
    };

    const handleLogout = () => {
        logout();
        navigate("/", { replace: true });
    };

    const handleOpenNotifications = (event) => {
        setNotificationsAnchor(event.currentTarget);
        fetchNotifications();
    };

    const handleCloseNotifications = () => {
        setNotificationsAnchor(null);
    };

    const handleOpenUserMenu = (event) => {
        setUserMenuAnchor(event.currentTarget);
    };

    const handleCloseUserMenu = () => {
        setUserMenuAnchor(null);
    };

    const handleOpenNotification = async (notification) => {
        try {
            if (!notification.isRead) {
                await axios.patch(`/notifications/${notification._id}/read`);
                setUnreadCount((current) => Math.max(current - 1, 0));
                setNotifications((current) =>
                    current.map((item) =>
                        item._id === notification._id
                            ? {
                                ...item,
                                isRead: true,
                            }
                            : item
                    )
                );
                window.dispatchEvent(new Event(NOTIFICATION_REFRESH_EVENT));
            }
        } catch {
            // Keep navigation working even if read state fails.
        }

        handleCloseNotifications();
        navigate(getNotificationPath(notification));
    };

    const userInitial = user?.name?.trim()?.charAt(0)?.toUpperCase() || "U";
    const hiddenNotificationCount = Math.max(notificationTotal - notifications.length, 0);

    return (
        <AppBar
            position="fixed"
            elevation={0}
            sx={{
                zIndex: (theme) => theme.zIndex.drawer + 1,
                borderBottom: "1px solid",
                borderColor: "rgba(148, 163, 184, 0.22)",
                backgroundColor: "rgba(255, 255, 255, 0.92)",
                backdropFilter: "blur(16px)",
                color: "text.primary",
                boxShadow: "0 10px 30px rgba(15, 23, 42, 0.04)",
            }}
        >
            <Toolbar
                sx={{
                    minHeight: { xs: 60, md: 68 },
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 2,
                    px: { xs: 1.25, md: 2.5 },
                }}
            >
                <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                    <IconButton
                        onClick={toggleSidebar}
                        aria-label={mobileOpen ? "Close navigation" : "Open navigation"}
                        sx={{
                            display: { md: "none" },
                            borderRadius: 2,
                            transition: "0.2s ease",
                            transform: mobileOpen ? "rotate(180deg)" : "rotate(0deg)",
                            "&:hover": {
                                backgroundColor: "rgba(37, 99, 235, 0.08)",
                            },
                        }}
                    >
                        {mobileOpen ? <CloseIcon /> : <MenuIcon />}
                    </IconButton>

                    <ButtonBase
                        onClick={handleBrandClick}
                        aria-label="Go to home"
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: { xs: 1, md: 1.25 },
                            minWidth: 0,
                            px: { xs: 0.5, md: 0.75 },
                            py: 0.75,
                            borderRadius: 2.5,
                            textAlign: "left",
                            transition: "0.2s ease",
                            "&:hover": {
                                backgroundColor: "rgba(37, 99, 235, 0.06)",
                                transform: "translateY(-1px)",
                            },
                        }}
                    >
                        <Box
                            component="img"
                            src={logo}
                            alt="MegaPay TMS"
                            sx={{
                                height: { xs: 30, md: 34 },
                                width: "auto",
                                objectFit: "contain",
                                display: "block",
                                flexShrink: 0,
                                filter: "drop-shadow(0 6px 10px rgba(37, 99, 235, 0.16))",
                            }}
                        />

                        <Typography
                            sx={{
                                fontWeight: 800,
                                fontSize: { xs: 17, md: 19 },
                                letterSpacing: "-0.02em",
                                lineHeight: 1,
                                color: "text.primary",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            MegaPay TMS
                        </Typography>
                    </ButtonBase>
                </Box>

                <Box
                    display="flex"
                    alignItems="center"
                    gap={{ xs: 1, sm: 1.5 }}
                    sx={{ flexShrink: 0 }}
                >
                    <IconButton
                        onClick={handleOpenNotifications}
                        aria-label="Open notifications"
                        sx={{
                            borderRadius: 2,
                            border: "1px solid rgba(148, 163, 184, 0.22)",
                            backgroundColor: "#fff",
                            "&:hover": {
                                backgroundColor: "rgba(37, 99, 235, 0.04)",
                            },
                        }}
                    >
                        <Badge badgeContent={unreadCount} color="error" max={99}>
                            <NotificationsRoundedIcon />
                        </Badge>
                    </IconButton>

                    <ButtonBase
                        onClick={handleOpenUserMenu}
                        aria-label="Open user menu"
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.1,
                            px: 1.2,
                            py: 0.7,
                            borderRadius: 2,
                            border: "1px solid rgba(148, 163, 184, 0.22)",
                            backgroundColor: "#fff",
                            boxShadow: "0 8px 18px rgba(15, 23, 42, 0.05)",
                            maxWidth: { xs: 160, sm: 220 },
                            "&:hover": {
                                backgroundColor: "rgba(37, 99, 235, 0.04)",
                            },
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 800,
                                color: "text.primary",
                                maxWidth: { xs: 92, sm: 140 },
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {user.name}
                        </Typography>
                        <Avatar
                            sx={{
                                width: 28,
                                height: 28,
                                fontSize: 13,
                                fontWeight: 800,
                                color: "text.secondary",
                                backgroundColor: "rgba(100, 116, 139, 0.14)",
                            }}
                        >
                            {userInitial}
                        </Avatar>
                    </ButtonBase>
                </Box>
            </Toolbar>

            <Menu
                anchorEl={notificationsAnchor}
                open={Boolean(notificationsAnchor)}
                onClose={handleCloseNotifications}
                PaperProps={{
                    sx: {
                        width: { xs: "calc(100vw - 24px)", sm: 360 },
                        maxWidth: 360,
                        borderRadius: 3,
                        border: "1px solid rgba(148,163,184,0.18)",
                        boxShadow: "0 20px 48px rgba(15,23,42,0.16)",
                        mt: 1,
                    },
                }}
            >
                <Box px={2} py={1.5}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                            Notifications
                        </Typography>
                        <Button size="small" onClick={() => {
                            handleCloseNotifications();
                            navigate("/notifications");
                        }}>
                            View All
                        </Button>
                    </Stack>
                </Box>
                <Divider />

                {notificationLoading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress size={24} />
                    </Box>
                ) : notificationError ? (
                    <Box px={2} py={3}>
                        <Typography variant="body2" color="error.main">
                            {notificationError}
                        </Typography>
                    </Box>
                ) : notifications.length ? (
                    <List disablePadding>
                        {notifications.map((notification, index) => (
                            <Box key={notification._id}>
                                <ListItemButton
                                    onClick={() => handleOpenNotification(notification)}
                                    sx={{
                                        alignItems: "flex-start",
                                        px: 2,
                                        py: 1.4,
                                        bgcolor: notification.isRead ? "transparent" : "rgba(37,99,235,0.05)",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 10,
                                            height: 10,
                                            borderRadius: "50%",
                                            bgcolor:
                                                getNotificationSeverityColor(notification.severity) === "critical"
                                                    ? "error.main"
                                                    : getNotificationSeverityColor(notification.severity) === "warning"
                                                        ? "warning.main"
                                                        : "info.main",
                                            mt: 0.9,
                                            mr: 1.25,
                                            flexShrink: 0,
                                        }}
                                    />
                                    <ListItemText
                                        primary={notification.title}
                                        secondary={
                                            <Box component="span" sx={{ display: "block", mt: 0.25 }}>
                                                <Typography
                                                    component="span"
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        display: "block",
                                                        lineHeight: 1.55,
                                                        mb: 0.4,
                                                        whiteSpace: "normal",
                                                    }}
                                                >
                                                    {notification.message}
                                                </Typography>
                                                <Typography component="span" variant="caption" color="text.secondary">
                                                    {formatNotificationDate(notification.createdAt)}
                                                </Typography>
                                            </Box>
                                        }
                                        primaryTypographyProps={{
                                            fontWeight: notification.isRead ? 700 : 800,
                                        }}
                                    />
                                </ListItemButton>
                                {index < notifications.length - 1 ? <Divider /> : null}
                            </Box>
                        ))}
                        {hiddenNotificationCount ? (
                            <>
                                <Divider />
                                <Box px={2} py={1.5}>
                                    <Typography variant="caption" color="text.secondary">
                                        +{hiddenNotificationCount} more notification{hiddenNotificationCount > 1 ? "s" : ""}
                                    </Typography>
                                </Box>
                            </>
                        ) : null}
                    </List>
                ) : (
                    <Box px={2} py={3}>
                        <Typography variant="body2" color="text.secondary">
                            No notifications yet.
                        </Typography>
                    </Box>
                )}
            </Menu>

            <Menu
                anchorEl={userMenuAnchor}
                open={Boolean(userMenuAnchor)}
                onClose={handleCloseUserMenu}
                PaperProps={{
                    sx: {
                        width: 148,
                        borderRadius: 2.5,
                        border: "1px solid rgba(148,163,184,0.18)",
                        boxShadow: "0 20px 48px rgba(15,23,42,0.16)",
                        mt: 0.75,
                        overflow: "hidden",
                    },
                }}
            >
                <List disablePadding>
                    <ListItemButton
                        onClick={() => {
                            handleCloseUserMenu();
                            handleLogout();
                        }}
                        sx={{
                            px: 1.75,
                            py: 1.1,
                            color: "error.main",
                            "&:hover": {
                                backgroundColor: "rgba(220, 38, 38, 0.06)",
                            },
                        }}
                    >
                        <ListItemText
                            primary="Logout"
                            primaryTypographyProps={{
                                fontWeight: 800,
                                fontSize: 13,
                            }}
                        />
                    </ListItemButton>
                </List>
            </Menu>
        </AppBar>
    );
}
